// Names the aisle an item's text belongs in. The ladder, first rule first:
//
// 1. A marker word decides wherever it stands ("dog treats"), unless a longer
//    phrase covers it ("hot dogs"). Two markers that disagree decide nothing.
// 2. Otherwise the longest dictionary phrase wins, and between equals the
//    rightmost, since an English noun phrase ends in its head: "white wine
//    vinegar" is vinegar, "milk chocolate" is chocolate.
// 3. A word that is often a modifier (chicken, milk, apple) counts alone only
//    as the item's last word, so "chicken tikka kit" stays in Other rather
//    than landing among the chickens.
// 4. A qualifier before the match, or in a trailing clause, moves it where the
//    dictionary says: "frozen peas" to Frozen, "dried apricots" to Snacks.
// 5. Alternatives joined by "or" must agree, or the item stays in Other.
// 6. Only when nothing above recognised the head, a trailing clause, the words
//    after a cut or a bracket, does spelling tolerance get a try.
//
// Only the text is read. Completion, quantity, notes, due dates and
// recurrence never reach this function.

import { rankOf, type CategoryId } from "./categories.js";
import type { Forms } from "./dictionary.js";
import { fuzzyMatch } from "./fuzzy.js";
import { lexicon, MAX_PHRASE_WORDS, type Entry } from "./lexicon.js";
import { NOISE_WORDS, parseItem, type Alternative, type Segment } from "./normalise.js";
import { CLASSIFIER_VERSION } from "./version.js";

export type MatchKind = "exact" | "phrase" | "fuzzy" | "conflict" | "none";

export interface Classification {
  readonly category: CategoryId;
  readonly rank: number;
  readonly kind: MatchKind;
  /** The dictionary alias that decided, in normal form. */
  readonly alias: string | null;
  /** The words of the item that matched it, in normal form. */
  readonly span: string | null;
  /** Edits between span and alias; zero unless the match is fuzzy. */
  readonly distance: number;
  /** The qualifier that moved the item, if one did. */
  readonly qualifier: keyof Forms | null;
  readonly version: string;
}

/** At most this many spelling-tolerant lookups per item. */
export const MAX_FUZZY_QUERIES = 6;

/** The aisles "frozen" moves an item out of, unless its group says otherwise. */
const FOOD: ReadonlySet<CategoryId> = new Set([
  "produce", "bakery", "meat-fish", "dairy", "ready-meals", "pantry", "snacks",
]);

const QUALIFIERS: ReadonlyMap<string, keyof Forms> = new Map([
  ["frozen", "frozen"],
  ["dried", "dried"],
  ["tinned", "tinned"], ["canned", "tinned"], ["tin", "tinned"], ["tins", "tinned"], ["can", "tinned"],
  ["cans", "tinned"], ["jar", "tinned"], ["jars", "tinned"], ["jarred", "tinned"],
  ["fresh", "fresh"],
]);

interface Found {
  readonly entry: Entry;
  readonly start: number;
  readonly span: string;
  readonly whole: boolean;
  readonly distance: number;
  readonly category: CategoryId;
  readonly qualifier: keyof Forms | null;
}

/** The index of the item's last word that is not a size, container or the like. */
function headIndex(words: Alternative): number {
  let head = words.length - 1;
  while (head > 0 && NOISE_WORDS.has(words[head] as string)) head -= 1;
  return head;
}

/** Applies the first qualifier, before the match or in a trailing clause, that moves it. */
function qualify(entry: Entry, before: readonly string[], trailing: readonly string[]) {
  for (const word of [...before, ...trailing]) {
    const form = QUALIFIERS.get(word);
    if (form === undefined) continue;
    const frozen = entry.forms.frozen ?? (FOOD.has(entry.category) ? "frozen" : undefined);
    const target = form === "frozen" ? frozen : entry.forms[form];
    if (target !== undefined && target !== entry.category) return { category: target, qualifier: form };
  }
  return { category: entry.category, qualifier: null };
}

function found(entry: Entry, words: Alternative, start: number, trailing: readonly string[], distance = 0): Found {
  const end = start + entry.words;
  const moved = qualify(entry, words.slice(0, start), trailing);
  return {
    entry,
    start,
    span: words.slice(start, end).join(" "),
    whole: start === 0 && end === words.length,
    distance,
    ...moved,
  };
}

interface Match { readonly entry: Entry; readonly start: number; readonly end: number }

function matchAlternative(words: Alternative, trailing: readonly string[]): Found | "conflict" | null {
  const { entries } = lexicon();
  const matches: Match[] = [];
  for (let start = 0; start < words.length; start += 1) {
    for (let n = 1; n <= MAX_PHRASE_WORDS && start + n <= words.length; n += 1) {
      const entry = entries.get(words.slice(start, start + n).join(" "));
      if (entry) matches.push({ entry, start, end: start + n });
    }
  }

  const covered = (marker: Match) => matches.some((other) => !other.entry.marker
    && other.entry.words > marker.entry.words && other.start <= marker.start && other.end >= marker.end);
  const markers = matches.filter((match) => match.entry.marker && !covered(match));
  const marker = markers[0];
  if (marker !== undefined) {
    if (markers.some((other) => other.entry.category !== marker.entry.category)) return "conflict";
    return found(marker.entry, words, marker.start, trailing);
  }

  const head = headIndex(words);
  let chosen: Match | undefined;
  for (const match of matches) {
    if (match.entry.marker) continue;
    const modifierOnly = match.entry.attributive && match.entry.words === 1 && match.start !== head;
    if (modifierOnly) continue;
    if (chosen === undefined) {
      chosen = match;
    } else if (match.entry.words > chosen.entry.words) {
      chosen = match;
    } else if (match.entry.words === chosen.entry.words && match.start > chosen.start) {
      chosen = match;
    }
  }
  return chosen === undefined ? null : found(chosen.entry, words, chosen.start, trailing);
}

/** Alternatives joined by "or" must agree on an aisle; unrecognised ones abstain. */
function matchSegment(segment: Segment, trailing: readonly string[]): Found | "conflict" | null {
  let agreed: Found | null = null;
  for (const alternative of segment) {
    const result = matchAlternative(alternative, trailing);
    if (result === "conflict") return "conflict";
    if (result === null) continue;
    if (agreed === null) agreed = result;
    else if (result.category !== agreed.category) return "conflict";
  }
  return agreed;
}

function fuzzySegment(segment: Segment, trailing: readonly string[]): Found | "conflict" | null {
  const { entries, fuzzy } = lexicon();
  let queries = 0;
  let agreed: Found | null = null;
  for (const words of segment) {
    const head = headIndex(words);
    const pair = head > 0 ? { start: head - 1, query: `${words[head - 1]} ${words[head]}` } : null;
    const single = { start: head, query: words[head] as string };
    let result: Found | null = null;
    for (const candidate of [pair, single]) {
      if (candidate === null || queries === MAX_FUZZY_QUERIES) continue;
      queries += 1;
      const hit = fuzzyMatch(candidate.query, fuzzy());
      const entry = hit ? entries.get(hit.alias) : undefined;
      if (hit && entry) {
        result = found(entry, words, candidate.start, trailing, hit.distance);
        break;
      }
    }
    if (result === null) continue;
    if (agreed === null) agreed = result;
    else if (agreed.category !== result.category) return "conflict";
  }
  return agreed;
}

function classification(category: CategoryId, kind: MatchKind, match: Found | null): Classification {
  return {
    category,
    rank: rankOf(category),
    kind,
    alias: match?.entry.alias ?? null,
    span: match?.span ?? null,
    distance: match?.distance ?? 0,
    qualifier: match?.qualifier ?? null,
    version: CLASSIFIER_VERSION,
  };
}

export function classifyGrocery(text: string): Classification {
  const parsed = parseItem(text);
  for (const segment of [parsed.head, ...parsed.fallbacks]) {
    const result = matchSegment(segment, parsed.trailing);
    if (result === "conflict") return classification("other", "conflict", null);
    if (result !== null) return classification(result.category, result.whole ? "exact" : "phrase", result);
  }
  const near = fuzzySegment(parsed.head, parsed.trailing);
  if (near === "conflict") return classification("other", "conflict", null);
  if (near !== null) return classification(near.category, "fuzzy", near);
  return classification("other", "none", null);
}
